import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { ExpandableRow, DetailPanel } from '../../components/ui/expandable-row';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  UserPlus,
  Search,
  Check,
  X,
  Clock,
  Users,
  Briefcase,
  Loader2,
  RefreshCw,
  Mail,
  Building2,
  Calendar,
  Phone,
  FileText,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminAanmeldingen = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aanmeldingen, setAanmeldingen] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, accepted: 0, rejected: 0, total: 0 });
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAanmelding, setSelectedAanmelding] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchAanmeldingen();
  }, [filterStatus]);

  const fetchAanmeldingen = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const response = await api.get(`/aanmeldingen?${params}`);
      setAanmeldingen(response.data.aanmeldingen || []);
      setCounts(response.data.counts || { pending: 0, accepted: 0, rejected: 0, total: 0 });
    } catch (error) {
      toast.error('Aanmeldingen laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (!selectedAanmelding || !actionType) return;
    
    setProcessing(true);
    try {
      await api.patch(`/aanmeldingen/${selectedAanmelding.id}?action=${actionType}&notes=${encodeURIComponent(notes)}`);
      toast.success(actionType === 'accept' ? 'Aanmelding geaccepteerd' : 'Aanmelding afgewezen');
      setShowActionModal(false);
      setSelectedAanmelding(null);
      setActionType(null);
      setNotes('');
      fetchAanmeldingen();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Actie mislukt'));
    } finally {
      setProcessing(false);
    }
  };

  const openActionModal = (aanmelding, action) => {
    setSelectedAanmelding(aanmelding);
    setActionType(action);
    setNotes('');
    setShowActionModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200"><Clock className="h-3 w-3 mr-1" />In afwachting</Badge>;
      case 'accepted':
        return <Badge className="bg-green-50 text-green-700 border-green-200"><Check className="h-3 w-3 mr-1" />Geaccepteerd</Badge>;
      case 'rejected':
        return <Badge className="bg-red-50 text-red-700 border-red-200"><X className="h-3 w-3 mr-1" />Afgewezen</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getRoleBadge = (rol) => {
    if (rol === 'partner' || rol === 'leverancier') {
      return <Badge className="bg-purple-50 text-purple-700 border-purple-200"><Briefcase className="h-3 w-3 mr-1" />Leverancier</Badge>;
    }
    return <Badge className="bg-blue-50 text-blue-700 border-blue-200"><Users className="h-3 w-3 mr-1" />Klant</Badge>;
  };

  // Filter by search
  const filteredAanmeldingen = aanmeldingen.filter(a => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      a.naam?.toLowerCase().includes(query) ||
      a.email?.toLowerCase().includes(query) ||
      a.bedrijfsnaam?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
              Admin
            </p>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Aanmeldingen
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Beheer aanmeldingen van de landingspagina
            </p>
          </div>
          <Button
            variant="outline"
            className="border-neutral-200"
            onClick={fetchAanmeldingen}
            data-testid="refresh-aanmeldingen"
          >
            <RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} />
            Vernieuwen
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card 
            className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] cursor-pointer transition-all hover:shadow-md ${filterStatus === 'all' ? 'ring-2 ring-black' : ''}`}
            onClick={() => setFilterStatus('all')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-neutral-100 text-neutral-600 flex items-center justify-center">
                  <UserPlus className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{counts.total}</p>
                  <p className="text-xs text-neutral-500">Totaal</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] cursor-pointer transition-all hover:shadow-md ${filterStatus === 'pending' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => setFilterStatus('pending')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-yellow-50 text-yellow-600 flex items-center justify-center">
                  <Clock className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{counts.pending}</p>
                  <p className="text-xs text-neutral-500">In afwachting</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] cursor-pointer transition-all hover:shadow-md ${filterStatus === 'accepted' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setFilterStatus('accepted')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                  <Check className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{counts.accepted}</p>
                  <p className="text-xs text-neutral-500">Geaccepteerd</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card 
            className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] cursor-pointer transition-all hover:shadow-md ${filterStatus === 'rejected' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => setFilterStatus('rejected')}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{counts.rejected}</p>
                  <p className="text-xs text-neutral-500">Afgewezen</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
          <Input
            placeholder="Zoek op naam, email of bedrijf..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="aanmelding-search"
          />
        </div>

        {/* Table */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardContent className="p-0">
            {filteredAanmeldingen.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="aanmeldingen-table">
                  <thead className="sticky top-0 z-10 bg-white dark:bg-neutral-900">
                    <tr className="border-b border-neutral-200 dark:border-neutral-700">
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Aanvrager</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Bedrijf</th>
                      <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Type</th>
                      <th className="text-center text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Status</th>
                      <th className="text-left text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Datum</th>
                      <th className="text-right text-xs font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 p-4 bg-neutral-50 dark:bg-neutral-800">Acties</th>
                      <th className="w-10 bg-neutral-50 dark:bg-neutral-800"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAanmeldingen.map((aanmelding, index) => (
                      <ExpandableRow
                        key={aanmelding.id}
                        className={`border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 ${
                          index % 2 === 1 ? 'bg-neutral-50/50 dark:bg-neutral-800/20' : ''
                        }`}
                        data-testid={`aanmelding-row-${aanmelding.id}`}
                        expandedContent={
                          <div className="space-y-4">
                            <DetailPanel
                              items={[
                                { label: 'Volledige naam', value: aanmelding.naam },
                                { label: 'E-mailadres', value: aanmelding.email },
                                { label: 'Telefoonnummer', value: aanmelding.telefoon || 'Niet opgegeven' },
                                { label: 'Bedrijfsnaam', value: aanmelding.bedrijfsnaam },
                                { label: 'KVK Nummer', value: aanmelding.kvk_nummer || 'Niet opgegeven' },
                                { label: 'Aangemeld op', value: new Date(aanmelding.created_at).toLocaleString('nl-NL') },
                              ]}
                            />
                            {aanmelding.bericht && (
                              <div className="space-y-1 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                  Bericht
                                </p>
                                <p className="text-sm text-neutral-700 dark:text-neutral-300 whitespace-pre-wrap">
                                  {aanmelding.bericht}
                                </p>
                              </div>
                            )}
                            {aanmelding.notes && (
                              <div className="space-y-1 pt-2 border-t border-neutral-200 dark:border-neutral-700">
                                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                  Admin notities
                                </p>
                                <p className="text-sm text-neutral-700 dark:text-neutral-300 italic">
                                  {aanmelding.notes}
                                </p>
                              </div>
                            )}
                          </div>
                        }
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-neutral-600">
                                {aanmelding.naam?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-black">{aanmelding.naam}</p>
                              <p className="text-xs text-neutral-500 flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {aanmelding.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-2 text-neutral-600">
                              <Building2 className="h-4 w-4" strokeWidth={1.5} />
                              {aanmelding.bedrijfsnaam || '-'}
                            </div>
                            {aanmelding.kvk_nummer && (
                              <div className="text-xs text-neutral-400 mt-0.5">
                                KVK: {aanmelding.kvk_nummer}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {getRoleBadge(aanmelding.rol)}
                        </td>
                        <td className="p-4 text-center">
                          {getStatusBadge(aanmelding.status)}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-sm text-neutral-500">
                            <Calendar className="h-4 w-4" strokeWidth={1.5} />
                            {new Date(aanmelding.created_at).toLocaleDateString('nl-NL')}
                          </div>
                        </td>
                        <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                          {aanmelding.status === 'pending' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 text-white hover:bg-green-700"
                                onClick={() => openActionModal(aanmelding, 'accept')}
                                data-testid={`accept-${aanmelding.id}`}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Accepteren
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => openActionModal(aanmelding, 'reject')}
                                data-testid={`reject-${aanmelding.id}`}
                              >
                                <X className="h-4 w-4 mr-1" />
                                Afwijzen
                              </Button>
                            </div>
                          )}
                          {aanmelding.status !== 'pending' && (
                            <span className="text-xs text-neutral-400">
                              {aanmelding.status === 'accepted' ? 'Geaccepteerd' : 'Afgewezen'}
                            </span>
                          )}
                        </td>
                      </ExpandableRow>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <UserPlus className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-1">Geen aanmeldingen</h3>
                <p className="text-sm text-neutral-500">
                  {filterStatus !== 'all' ? 'Geen aanmeldingen met deze status' : 'Er zijn nog geen aanmeldingen binnengekomen'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accept' ? 'Aanmelding Accepteren' : 'Aanmelding Afwijzen'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'accept' 
                ? 'Na acceptatie kunt u via Gebruikersbeheer een account aanmaken voor deze aanvrager.'
                : 'Geef optioneel een reden op voor de afwijzing.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAanmelding && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-neutral-50 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-500">Naam:</span>
                  <span className="text-sm font-medium">{selectedAanmelding.naam}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-500">Email:</span>
                  <span className="text-sm font-medium">{selectedAanmelding.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-500">Bedrijf:</span>
                  <span className="text-sm font-medium">{selectedAanmelding.bedrijfsnaam}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-neutral-500">Type:</span>
                  {getRoleBadge(selectedAanmelding.rol)}
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {actionType === 'accept' ? 'Notities (optioneel)' : 'Reden afwijzing (optioneel)'}
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={actionType === 'accept' ? 'Eventuele notities...' : 'Reden voor afwijzing...'}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Annuleren
            </Button>
            <Button 
              className={actionType === 'accept' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleAction}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : actionType === 'accept' ? (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Accepteren
                </>
              ) : (
                <>
                  <X className="h-4 w-4 mr-1" />
                  Afwijzen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminAanmeldingen;
